/**
 * Odoo Import Settings Page
 *
 * Allows importing data from Odoo ERP systems or local export files.
 */

import { useState, useCallback } from 'react';
import {
  Typography,
  Card,
  Form,
  Input,
  Button,
  Space,
  Alert,
  Select,
  Divider,
  Steps,
  Result,
  InputNumber,
  message,
  Radio,
} from 'antd';
import {
  CloudServerOutlined,
  CheckCircleOutlined,
  ImportOutlined,
  LoadingOutlined,
  FileOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation } from '@tanstack/react-query';
import { odooApi } from '@api/index';

const { Title, Text, Paragraph } = Typography;

type ImportMode = 'file' | 'connection';

export default function OdooImportPage() {
  const [form] = Form.useForm();
  const [importMode, setImportMode] = useState<ImportMode>('file');
  const [currentStep, setCurrentStep] = useState(0);
  const [connectionVerified, setConnectionVerified] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<{ name?: string; company?: string } | null>(null);
  const [selectedModel, setSelectedModel] = useState<string>('project.task');
  const [selectedFile, setSelectedFile] = useState<string>('odoo_projects_data.sql');

  // Fetch available models
  const { data: models = [] } = useQuery({
    queryKey: ['odoo', 'models'],
    queryFn: () => odooApi.getModels(),
  });

  // Fetch available files
  const { data: files = [], isLoading: filesLoading } = useQuery({
    queryKey: ['odoo', 'files'],
    queryFn: () => odooApi.getFiles(),
  });

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: odooApi.testConnection,
    onSuccess: (result) => {
      if (result.success) {
        setConnectionVerified(true);
        setConnectionInfo({ name: result.name, company: result.company });
        setCurrentStep(1);
        message.success('Connection successful!');
      } else {
        setConnectionVerified(false);
        message.error(result.error || 'Connection failed');
      }
    },
  });

  // Connection-based import mutation
  const importMutation = useMutation({
    mutationFn: odooApi.importData,
    onSuccess: (result) => {
      if (result.success) {
        setCurrentStep(2);
      } else {
        message.error(result.message || 'Import failed');
      }
    },
  });

  // File-based import mutation
  const fileImportMutation = useMutation({
    mutationFn: odooApi.importFromFile,
    onSuccess: (result) => {
      if (result.success) {
        setCurrentStep(2);
      } else {
        message.error(result.message || 'Import failed');
      }
    },
  });

  const handleTestConnection = useCallback(async () => {
    try {
      const values = await form.validateFields(['url', 'database', 'username', 'password']);
      testConnectionMutation.mutate(values);
    } catch {
      // Validation failed
    }
  }, [form, testConnectionMutation]);

  const handleImport = useCallback(async () => {
    try {
      const values = await form.validateFields();
      importMutation.mutate({
        ...values,
        model: selectedModel,
        limit: values.limit || 100,
      });
    } catch {
      // Validation failed
    }
  }, [form, selectedModel, importMutation]);

  const handleFileImport = useCallback(async () => {
    try {
      const values = await form.validateFields(['limit']);
      fileImportMutation.mutate({
        filename: selectedFile,
        limit: values.limit || 1000,
      });
    } catch {
      // Validation failed
    }
  }, [form, selectedFile, fileImportMutation]);

  const handleReset = useCallback(() => {
    setCurrentStep(0);
    setConnectionVerified(false);
    setConnectionInfo(null);
    form.resetFields();
  }, [form]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get the active mutation based on import mode
  const activeMutation = importMode === 'file' ? fileImportMutation : importMutation;

  return (
    <div style={{ maxWidth: 800 }}>
      <Title level={3}>Odoo Import</Title>
      <Paragraph type="secondary">
        Import data from Odoo export files or connect to a live Odoo ERP system.
      </Paragraph>

      {currentStep === 0 && (
        <Radio.Group
          value={importMode}
          onChange={(e) => setImportMode(e.target.value)}
          style={{ marginBottom: 24 }}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="file">
            <FileOutlined /> Import from File
          </Radio.Button>
          <Radio.Button value="connection">
            <CloudServerOutlined /> Connect to Odoo
          </Radio.Button>
        </Radio.Group>
      )}

      <Steps
        current={currentStep}
        style={{ marginBottom: 32 }}
        items={
          importMode === 'file'
            ? [
                { title: 'Select File', icon: <FolderOpenOutlined /> },
                { title: 'Import', icon: <ImportOutlined /> },
                { title: 'Complete', icon: <CheckCircleOutlined /> },
              ]
            : [
                { title: 'Connect', icon: <CloudServerOutlined /> },
                { title: 'Configure', icon: <ImportOutlined /> },
                { title: 'Complete', icon: <CheckCircleOutlined /> },
              ]
        }
      />

      {currentStep === 0 && importMode === 'file' && (
        <Card title="Step 1: Select Export File">
          <Alert
            message="File Import"
            description="Select an Odoo SQL export file to import. The file should contain project tasks data exported from Odoo."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form form={form} layout="vertical">
            <Form.Item label="Available Files">
              {filesLoading ? (
                <LoadingOutlined />
              ) : files.length === 0 ? (
                <Alert
                  message="No export files found"
                  description="Place Odoo export files (*.sql) in the project root directory with prefix 'odoo_'"
                  type="warning"
                />
              ) : (
                <Select
                  value={selectedFile}
                  onChange={setSelectedFile}
                  style={{ width: '100%' }}
                  options={files.map((f) => ({
                    value: f.filename,
                    label: (
                      <Space>
                        <FileOutlined />
                        <span>{f.filename}</span>
                        <Text type="secondary">({formatFileSize(f.size)})</Text>
                      </Space>
                    ),
                  }))}
                />
              )}
            </Form.Item>

            <Form.Item
              name="limit"
              label="Maximum Records to Import"
              initialValue={1000}
            >
              <InputNumber min={1} max={10000} style={{ width: 200 }} />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                icon={<ImportOutlined />}
                onClick={handleFileImport}
                loading={fileImportMutation.isPending}
                disabled={files.length === 0}
              >
                Start Import
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}

      {currentStep === 0 && importMode === 'connection' && (
        <Card title="Step 1: Connect to Odoo">
          <Alert
            message="Connection Details"
            description="Enter your Odoo instance URL and credentials. You can use either your password or an API key for authentication."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form form={form} layout="vertical">
            <Form.Item
              name="url"
              label="Odoo URL"
              rules={[
                { required: true, message: 'Please enter Odoo URL' },
                { type: 'url', message: 'Please enter a valid URL' },
              ]}
            >
              <Input placeholder="https://your-company.odoo.com" />
            </Form.Item>

            <Form.Item
              name="database"
              label="Database Name"
              rules={[{ required: true, message: 'Please enter database name' }]}
            >
              <Input placeholder="your_database" />
            </Form.Item>

            <Form.Item
              name="username"
              label="Username / Email"
              rules={[{ required: true, message: 'Please enter username' }]}
            >
              <Input placeholder="admin@company.com" />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password / API Key"
              rules={[{ required: true, message: 'Please enter password' }]}
            >
              <Input.Password placeholder="••••••••" />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                icon={
                  testConnectionMutation.isPending ? (
                    <LoadingOutlined />
                  ) : connectionVerified ? (
                    <CheckCircleOutlined />
                  ) : (
                    <CloudServerOutlined />
                  )
                }
                onClick={handleTestConnection}
                loading={testConnectionMutation.isPending}
              >
                Test Connection
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}

      {currentStep === 1 && (
        <Card title="Step 2: Configure Import">
          <Alert
            message={`Connected as ${connectionInfo?.name || 'User'}`}
            description={connectionInfo?.company && `Company: ${connectionInfo.company}`}
            type="success"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form form={form} layout="vertical">
            <Form.Item label="Data to Import">
              <Select
                value={selectedModel}
                onChange={setSelectedModel}
                options={models.map((m) => ({
                  value: m.model,
                  label: (
                    <Space direction="vertical" size={0}>
                      <Text strong>{m.name}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {m.description}
                      </Text>
                    </Space>
                  ),
                }))}
              />
            </Form.Item>

            <Form.Item
              name="limit"
              label="Maximum Records"
              initialValue={100}
            >
              <InputNumber min={1} max={1000} style={{ width: 200 }} />
            </Form.Item>

            <Divider />

            <Space>
              <Button onClick={() => setCurrentStep(0)}>Back</Button>
              <Button
                type="primary"
                icon={<ImportOutlined />}
                onClick={handleImport}
                loading={importMutation.isPending}
              >
                Start Import
              </Button>
            </Space>
          </Form>
        </Card>
      )}

      {currentStep === 2 && (
        <Card>
          <Result
            status={activeMutation.data?.success ? 'success' : 'error'}
            title={activeMutation.data?.success ? 'Import Complete!' : 'Import Failed'}
            subTitle={activeMutation.data?.message}
            extra={
              <Space direction="vertical" size="middle">
                {activeMutation.data?.success && (
                  <Space size="large">
                    <Card size="small">
                      <Statistic title="Imported" value={activeMutation.data.imported} />
                    </Card>
                    <Card size="small">
                      <Statistic title="Skipped" value={activeMutation.data.skipped} />
                    </Card>
                    <Card size="small">
                      <Statistic title="Errors" value={activeMutation.data.errors} />
                    </Card>
                  </Space>
                )}
                <Button type="primary" onClick={handleReset}>
                  Import More
                </Button>
              </Space>
            }
          />
        </Card>
      )}
    </div>
  );
}

// Simple statistic component
function Statistic({ title, value }: { title: string; value: number }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {title}
      </Text>
      <div style={{ fontSize: 24, fontWeight: 'bold' }}>{value}</div>
    </div>
  );
}
